import React, { Component } from 'react';
import { reduxForm } from 'redux-form';
import { connect } from 'react-redux';

// Components
import Form from 'Common/Form';
import TextArea from 'Common/TextArea';
import InputForm from 'Common/InputForm';
import Button from 'Common/Button';
import PropTypes from 'prop-types';
import styles from 'components/DealerForm.scss';
import classNames from 'classnames';
import { gtmEventPush } from 'utils/gtm/events';

/**
 * <DealerForm /> component.
 * @return {class} new DealerForm instance
 */
export class DealerForm extends Component<*, *> {
  /**
   *  Props implementation.
   */
  static propTypes = {
    extendStyle: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    formRef: PropTypes.object,
    subject: PropTypes.string,
    item: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    postCode: PropTypes.string,
    formType: PropTypes.string,
    make: PropTypes.string,
    model: PropTypes.string,
    price: PropTypes.string,
    changeFormType: PropTypes.func,
    onSuccess: PropTypes.func
  };

  onBlurOverride = e => {
    gtmEventPush({
      method: 'enquiryFormValuesBlur',
      args: {
        callbackOrMessage: this.props.formType,
        itemLabel: e.target.value
      }
    });
  };

  onFormTypeMessage = e =>
    e.target.checked && this.props.changeFormType('message');

  onFormTypeCallback = e =>
    e.target.checked && this.props.changeFormType('callback');

  /**
   * Renders DealerForm.
   *
   * @return {JSX} rendered DealerForm.
   */

  render(): Element<*> {
    const {
      classDealerForm,
      classDealerFormParagraph,
      classDealerFormNewsletter,
      classConsentLabel,
      classFormTypeLabel,
      classFormSubmitContainer,
      classFormSubmitResponse,
      classFormSuccess,
      classFormError
    } = styles;
    const dealerName = this.props.item.dealer
      ? this.props.item.dealer.name
      : 'the seller';

    const isCallbackForm = this.props.formType === 'callback';
    const messagePlaceHolder = isCallbackForm
      ? 'Write your message and your preferred time to get a callback here'
      : 'Write your message here';
    const buttonText = isCallbackForm ? 'Request callback' : 'Message dealer';

    const { make, model, price } = this.props;
    const defaultMessage = `Hi, I'm interested in this ${make}/${model} with a listing price of ${price}. Please could you contact me concerning this vehicle.`;

    return (
      <div className={classNames([classDealerForm, this.props.extendStyle])}>
        <Form
          title={`Contact dealer about ${this.props.subject}`}
          item={this.props.item}
          buttonText={buttonText}
          formRef={this.props.formRef}
          successText={`Thanks for your enquiry! It has been sent to ${dealerName}, who will be in touch shortly.`}
          formType={this.props.formType}
          submissionRender={(success, resetForm) => (
            <div className={classFormSubmitContainer}>
              <div
                className={classNames(
                  classFormSubmitResponse,
                  success ? classFormSuccess : classFormError
                )}
              >
                <p>
                  {success
                    ? `Thanks for your enquiry! It has been sent to ${dealerName}, who will be in touch shortly.`
                    : 'Something went wrong with the form. Please try again later.'}
                </p>
              </div>
              <Button
                buttonText={success ? 'Return to detail' : 'Try again'}
                onClick={success ? this.props.onSuccess : resetForm}
              />
            </div>
          )}
        >
          <div className={styles.formSelector}>
            <p>I would like to:</p>
            <div className={styles.selectorRadios}>
              <InputForm
                classLabel={classFormTypeLabel}
                extendStyle={classDealerFormNewsletter}
                label={'Message dealer'}
                type="radio"
                name="formType"
                value="message"
                checked={!isCallbackForm}
                onChange={this.onFormTypeMessage}
                onBlur={this.onBlurOverride}
              />
              <InputForm
                classLabel={classFormTypeLabel}
                extendStyle={classDealerFormNewsletter}
                label={'Request a callback'}
                type="radio"
                name="formType"
                value="callback"
                checked={isCallbackForm}
                onChange={this.onFormTypeCallback}
                onBlur={this.onBlurOverride}
              />
            </div>
          </div>
          <div className={styles.formContent}>
            <div className={styles.column}>
              <InputForm
                required={true}
                name="name"
                placeholder={'Please enter your full name'}
              />
              {!isCallbackForm && (
                <InputForm
                  required={true}
                  name="email"
                  valType={'email'}
                  placeholder={'Please enter a valid email'}
                />
              )}
              <InputForm
                required={isCallbackForm || false}
                valType={'number'}
                type="tel"
                name="phone"
                placeholder={'Please enter your phone number'}
              />
              <InputForm
                required={true}
                valType={'postcode'}
                name="postcode"
                value={this.props.postCode}
                placeholder={'Please enter your postcode'}
              />
            </div>
            <div className={styles.column} key={'col2'}>
              <TextArea
                extendStyle={styles.textArea}
                required={true}
                name="message"
                placeholder={messagePlaceHolder}
                value={defaultMessage}
              />
              <p className={classDealerFormParagraph}>
                By clicking {buttonText}, you agree for us to share these
                details with heycar to allow this specific dealer to contact you
                about your query.{' '}
                <a
                  href="https://heycar.co.uk/privacy"
                  target="_blank"
                  rel="noreferrer"
                >
                  heycar&apos;s <strong>privacy notice</strong>.
                </a>
              </p>

              <p className={classDealerFormParagraph}>
                Keep me updated with the latest advice, news, and offers from{' '}
                heycar:
              </p>

              <span className={styles.updateCheckboxes}>
                <InputForm
                  extendStyle={classDealerFormNewsletter}
                  label="by phone"
                  classLabel={classConsentLabel}
                  name="heycarConsentPhone"
                  type={'checkbox'}
                />
                <InputForm
                  extendStyle={classDealerFormNewsletter}
                  label="by email"
                  classLabel={classConsentLabel}
                  name="heycarConsentEmail"
                  type={'checkbox'}
                />
              </span>
            </div>
          </div>
        </Form>
      </div>
    );
  }
}

let DealerFormWrapper = reduxForm({
  form: 'filter',
  destroyOnUnmount: false
})(DealerForm);

DealerFormWrapper = connect(state => ({
  initialValues: state.form.search ? state.form.search.values : {}
}))(DealerFormWrapper);

export default DealerFormWrapper;



// WEBPACK FOOTER //
// ./src/Detail/DealerForm/index.js